import { useCallback, useEffect, useState } from "react";
import { Building2, Smartphone, QrCode } from "lucide-react";
import {
  fetchPaymentMethods,
  type PaymentMethod,
} from "../src/api/payment-methods";
import { formatBankAccountForRegion } from "../utils/regions";
import { useUserProfile } from "./UserProfileContext";
import { Button } from "./ui/button";
import { QuickPayQRCode } from "./QuickPayQRCode";
import { createQuickPayLinkForMethod } from "../utils/quickPay";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  getPayoutMethodLabel,
  isPayoutMethod,
} from "../utils/paymentMethods";

interface BankAccountsSectionProps {
  onNavigate: (tab: string, data?: any) => void;
}

export function BankAccountsSection({ onNavigate }: BankAccountsSectionProps) {
  const { appSettings } = useUserProfile();
  const [payoutAccounts, setPayoutAccounts] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [quickPayDialogOpen, setQuickPayDialogOpen] = useState(false);
  const [quickPayTarget, setQuickPayTarget] = useState<PaymentMethod | null>(null);
  const [quickPayMethodLabel, setQuickPayMethodLabel] = useState("");
  const [quickPayLink, setQuickPayLink] = useState<string | null>(null);
  const [quickPayLoading, setQuickPayLoading] = useState(false);
  const [quickPayError, setQuickPayError] = useState<string | null>(null);

  useEffect(() => {
    const loadPaymentAccounts = async () => {
      try {
        const methods = await fetchPaymentMethods();
        const accounts = methods.filter(isPayoutMethod);
        setPayoutAccounts(accounts);
      } catch (error) {
        console.error("Failed to load bank accounts:", error);
      } finally {
        setLoading(false);
      }
    };

    loadPaymentAccounts();
  }, []);

  const hasPayoutAccounts = payoutAccounts.length > 0;
  const ctaLabel = hasPayoutAccounts
    ? "Add an Extra Bank Account"
    : "Link a Bank Account";

  const maskFormattedValue = (value: string) => {
    const normalizedChars = value.replace(/[^0-9A-Za-z]/g, "");
    if (normalizedChars.length <= 4) {
      return value;
    }

    const hiddenChars = normalizedChars.length - 4;
    let maskedCount = 0;

    return value
      .split("")
      .map((char) => {
        if (!/[0-9A-Za-z]/.test(char)) {
          return char;
        }
        if (maskedCount < hiddenChars) {
          maskedCount += 1;
          return "*";
        }
        return char;
      })
      .join("");
  };

  const getDisplayValue = (account: PaymentMethod) => {
    if (account.type === "bank") {
      if (!account.accountNumber) {
        return "Account number unavailable";
      }
      const formatted = formatBankAccountForRegion(
        appSettings.region,
        account.accountNumber,
      );
      return formatted
        ? maskFormattedValue(formatted)
        : "Account number unavailable";
    }

    const mobileIdentifier = account.phoneNumber || account.accountNumber;
    if (!mobileIdentifier) {
      return "Account number unavailable";
    }

    return maskFormattedValue(mobileIdentifier);
  };

  const loadQuickPayLink = useCallback(
    async (account: PaymentMethod) => {
      setQuickPayLoading(true);
      setQuickPayError(null);
      setQuickPayLink(null);
      const methodLabel = getPayoutMethodLabel(account);
      setQuickPayMethodLabel(methodLabel);
      try {
        const { url } = await createQuickPayLinkForMethod({
          paymentMethodId: account.id,
          region: appSettings.region,
          title: `Quick pay ${methodLabel}`,
          message: `Scan to pay ${methodLabel}.`,
        });
        setQuickPayLink(url);
      } catch (error) {
        console.error("Unable to load quick pay link", error);
        const message =
          error instanceof Error
            ? error.message
            : "Unable to generate quick pay link right now.";
        setQuickPayError(message);
      } finally {
        setQuickPayLoading(false);
      }
    },
    [appSettings.region],
  );

  useEffect(() => {
    if (!quickPayDialogOpen || !quickPayTarget) return;
    void loadQuickPayLink(quickPayTarget);
  }, [quickPayDialogOpen, quickPayTarget, loadQuickPayLink]);

  const handleQuickPayButton = (account: PaymentMethod) => {
    setQuickPayTarget(account);
    setQuickPayDialogOpen(true);
  };

  const handleQuickPayDialogChange = (open: boolean) => {
    setQuickPayDialogOpen(open);
    if (!open) {
      setQuickPayTarget(null);
      setQuickPayLink(null);
      setQuickPayError(null);
      setQuickPayMethodLabel("");
    }
  };

  const handleQuickPayRetry = () => {
    if (quickPayTarget) {
      void loadQuickPayLink(quickPayTarget);
    }
  };

  const renderAccounts = () => {
    if (loading) {
      return (
        <div className="space-y-[16px]">
          {[0, 1].map((index) => (
            <div key={index} className="flex gap-[12px] items-center">
              <div className="h-[40px] w-[40px] rounded-full bg-muted animate-pulse" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted rounded animate-pulse w-1/2" />
                <div className="h-3 bg-muted rounded animate-pulse w-1/3" />
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (!hasPayoutAccounts) {
      return (
        <div className="flex flex-col gap-[8px]">
          <p
            className="font-['Roboto:Medium',_sans-serif] text-[14px] leading-[21px] text-foreground"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            No payout accounts yet
          </p>
          <p
            className="font-['Roboto:Regular',_sans-serif] text-[12.25px] leading-[17.5px] text-muted-foreground"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            Link a bank account or mobile wallet so payouts and bill splits can
            arrive in the right place. Your linked accounts will show here.
          </p>
        </div>
      );
    }

    return payoutAccounts.slice(0, 2).map((account, index) => {
      const Icon = account.type === "bank" ? Building2 : Smartphone;
      const displayName =
        account.type === "bank"
          ? account.bank || "Bank Account"
          : account.provider || "Mobile Money";

      const quickPayLabel = getPayoutMethodLabel(account);
      return (
        <div key={account.id}>
          <div className="flex gap-[12px] items-center">
            <div className="bg-blue-100 p-[10px] rounded-full flex items-center justify-center shrink-0">
              <Icon className="h-[20px] w-[20px] text-blue-600" />
            </div>

            <div className="flex-1 flex items-center gap-[8px]">
              <div className="flex-1 flex flex-col gap-[4px]">
                <p
                  className="font-['Roboto:Medium',_sans-serif] font-medium leading-[21px] text-[14px] text-foreground"
                  style={{ fontVariationSettings: "'wdth' 100" }}
                >
                  {displayName}
                </p>
                <p
                  className="font-['Roboto:Regular',_sans-serif] font-normal leading-[17.5px] text-muted-foreground text-[12.25px]"
                  style={{ fontVariationSettings: "'wdth' 100" }}
                >
                  {getDisplayValue(account)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                className="shrink-0 border border-border text-muted-foreground hover:text-primary"
                onClick={() => handleQuickPayButton(account)}
                aria-label={`Show quick pay QR for ${quickPayLabel}`}
              >
                <QrCode className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {index < Math.min(payoutAccounts.length, 2) - 1 && (
            <div className="h-[1px] bg-border mt-[16px]" />
          )}
        </div>
      );
    });
  };

  return (
    <div className="content-stretch flex flex-col gap-[13.987px] items-start w-full">
      <div className="h-[39.998px] relative shrink-0 w-full">
        <div className="flex flex-row items-center size-full">
          <div className="content-stretch flex h-[39.998px] items-center justify-between relative w-full">
            <div className="h-[21px] relative shrink-0">
              <div className="bg-clip-padding border-0 border-[transparent] border-solid box-border content-stretch flex h-[21px] items-start relative">
                <p
                  className="font-['Roboto:Regular',_sans-serif] font-normal leading-[21px] relative shrink-0 text-[14px] text-foreground text-nowrap whitespace-pre"
                  style={{ fontVariationSettings: "'wdth' 100" }}
                >
                  Bank Accounts
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-card box-border flex flex-col p-[20px] relative rounded-[16px] shrink-0 w-full">
        <div
          aria-hidden="true"
          className="absolute border-[1.268px] border-border border-solid inset-0 pointer-events-none rounded-[16px]"
        />

        <div className="flex flex-col gap-[16px]">
          {renderAccounts()}

          <Button
            onClick={() =>
              onNavigate("payment-methods", {
                from: "home",
                startAddFlow: true,
              })
            }
            className="mt-[8px] w-full justify-center bg-primary text-primary-foreground hover:bg-primary/90 text-[14px] font-semibold"
          >
            {ctaLabel}
          </Button>
        </div>
      </div>

      <Dialog open={quickPayDialogOpen} onOpenChange={handleQuickPayDialogChange}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Quick Pay</DialogTitle>
            <DialogDescription>
              Share this QR so payers scan and land on your secure payment page.
            </DialogDescription>
          </DialogHeader>
          <QuickPayQRCode
            url={quickPayLink}
            loading={quickPayLoading}
            error={quickPayError}
            onRetry={handleQuickPayRetry}
            methodLabel={quickPayMethodLabel}
            helperText="Share this QR so payers land on your secure quick pay page."
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
